import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  FormHelperText,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { ClearIcon } from "@mui/x-date-pickers";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  IndentCreate,
  IndentUpdate,
  ApproveSolistapi,
  getDivisionUnits,
  getCategories,
  fetchSubCategories,
  fetchItemsByCategoryAndSubcategory,
  GetNextIndentNo,
  getSizes,
  getItemType,
} from "../../../Services/Services.jsx";
import AddItemForm from "../Inventory/ItemForm";
import { useCallback } from "react";
import { debounce } from "lodash";
import { CircularProgress } from "@mui/material";

const IndentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // ✅ Get division from localStorage ONLY
  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [openItemModal, setOpenItemModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [items, setItems] = useState({});
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([createEmptyRow()]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [size, setSizes] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [validationMessages, setValidationMessages] = useState([]);

  const isEditMode = !!location.state?.data;
  const [formData, setFormData] = useState({
     division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    indent_type: "",
    form_remarks: "",
    department_name: "",
    project: "",
    indent_no: "",
    created_by: localStorage.getItem("user_id"),
  });

  // -------------------- Utility --------------------
  function createEmptyRow() {
    return {
      category: null,
      sub_category: null,
      item: null,
      uom: "",
      item_description: "",
      required_qty: "",
      stock_qty: "",
      indent_qty: "",
      previous_purchase_rate: "",
      estimated_rate: "",
      estimated_value: "",
      cost_center: "",
      size: null,
      ItemType: null,
    };
  }

  // -------------------- Fetch dropdown data --------------------//
  useEffect(() => {
    (async () => {
      try {
        const [division_unit, cat, sizeData, itemTypeData] = await Promise.all([
          getDivisionUnits(),
          getCategories(),
          getSizes(),
          getItemType(),
        ]);
        setUnitOptions(division_unit.units || []);
        setCategories(Array.isArray(cat) ? cat : cat?.results || []);
        setSizes(Array.isArray(sizeData) ? sizeData : sizeData?.results || []);
        setItemTypes(
          Array.isArray(itemTypeData)
            ? itemTypeData
            : itemTypeData?.results || []
        );
        setIsDataLoaded(true);
        const divisionData = getSelectedDivision();
        if (divisionData?.division_id && !isEditMode) {
          try {
            const res = await GetNextIndentNo(divisionData.division_id);
            if (res?.indent_no) {
              setFormData((prev) => ({ ...prev, indent_no: res.indent_no }));
            }
          } catch (err) {
            console.error("Failed to fetch indent number:", err);
          }
          fetchProjects(1, {}, divisionData.division_id);
        }
      } catch (error) {
        toast.error("Failed to fetch dropdown data");
      }
    })();
  }, []);

    const fetchProjects = async (page = 1, filters = {}) => {
    try {
      setProjectLoading(true);
      const divisionData = getSelectedDivision();
      const divisionId = divisionData?.division_id;
      
      const finalFilters = divisionId
        ? { ...filters, division: divisionId }
        : filters;

      const response = await ApproveSolistapi(page, finalFilters);

      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      if (page === 1) {
        setProjectOptions(results);
      } else {
        setProjectOptions((prev) => [...prev, ...results]);
      }

      setProjectPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProjects(1, { project_name: projectSearch });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [projectSearch, isEditMode]);

  // -------------------- Populate data in edit mode --------------------
  useEffect(() => {
    if (isEditMode && isDataLoaded) {
      const incoming = location.state.data;

      setFormData({
        indent_no: incoming.indent_no || "",
        indent_type: incoming.indent_type || "",
        form_remarks: incoming.form_remarks || "",
        division: incoming.division || "",
        department_name: incoming.department_name || "",
        project: incoming.project || "",
        created_by: incoming.created_by || localStorage.getItem("user_id"),
      });

      const processItems = async () => {
        const mappedItems = await Promise.all(
          (incoming.items || []).map(async (item, index) => {
            const foundItemType = item.ItemType
              ? itemTypes.find((t) => String(t.id) === String(item.ItemType))
              : null;

            const foundSize = item.size
              ? size.find((s) => String(s.id) === String(item.size))
              : null;

            const foundCategory = item.category
              ? categories.find((c) => String(c.id) === String(item.category))
              : null;

            let subcatList = [];
            if (item.category) {
              try {
                subcatList = await fetchSubCategories(item.category);
                setSubcategories((prev) => ({
                  ...prev,
                  [index]: subcatList,
                }));
              } catch (error) {
                console.error("Error fetching subcategories:", error);
              }
            }

            const foundSubCategory =
              item.sub_category && subcatList.length > 0
                ? subcatList.find(
                    (s) => String(s.subcat_id) === String(item.sub_category),
                  )
                : null;

            let itemList = [];
            if (item.category && item.sub_category) {
              try {
                const itemData = await fetchItemsByCategoryAndSubcategory(
                  item.category,
                  item.sub_category,
                );
                itemList = itemData.results || [];
                setItems((prev) => ({
                  ...prev,
                  [index]: itemList.map((item) => ({
                    id: item.id,
                    name: item.name,
                    item_code: item.item_code || item.code || "",
                    unit_name: item.uom || item.unit_name || "",
                    current_stock: item.current_stock,
                    size_id: item.size || null,
                    size_name: item.size_name || "",
                    store_item_type_id: item.store_item_type || null,
                    store_item_type_name: item.store_item_type_name || "",
                    unit_id: item.unit || null,
                  })),
                }));
              } catch (error) {
                console.error("Error fetching items:", error);
              }
            }

            const foundItem =
              item.item && itemList.length > 0
                ? itemList.find((i) => String(i.id) === String(item.item))
                : null;

            return {
              category: foundCategory || {
                id: item.category || item.category?.id,
                name: item.category?.name || item.category_name || "",
              },
              sub_category: foundSubCategory || {
                subcat_id: item.sub_category || item.sub_category?.subcat_id,
                name: item.sub_category?.name || item.sub_category_name || "",
              },
              item: foundItem
                ? {
                    id: foundItem.id,
                    name: foundItem.name,
                    item_code: foundItem.item_code || "",
                    unit_name: foundItem.unit_name || item.uom,
                    current_stock: foundItem.current_stock || item.stock_qty,
                    size_id: foundItem.size_id,
                    size_name: foundItem.size_name,
                    store_item_type_id: foundItem.store_item_type_id,
                    store_item_type_name: foundItem.store_item_type_name,
                    unit_id: foundItem.unit_id,
                  }
                : {
                    id: item.item || item.item?.id,
                    name: item.item?.name || item.item_name || "",
                    item_code: item.item_code || "",
                    unit_name: item.uom,
                    current_stock: item.stock_qty,
                    size_id: item.size,
                    size_name: "",
                    store_item_type_id: item.ItemType,
                    store_item_type_name: "",
                    unit_id: null,
                  },
              ItemType: foundItemType,
              size: foundSize,
              uom: item.uom,
              item_description: item.item_description || "",
              required_qty: item.required_qty,
              stock_qty: item.stock_qty,
              indent_qty: item.indent_qty,
              previous_purchase_rate: item.previous_purchase_rate,
              estimated_rate: item.estimated_rate,
              estimated_value: item.estimated_value,
              cost_center: item.cost_center || "",
            };
          }),
        );

        setRows(mappedItems);
      };

      processItems();
    }
  }, [isEditMode, isDataLoaded, location.state, categories, size, itemTypes]);

  // Add this useEffect after your existing useEffects
  useEffect(() => {
    const stored = localStorage.getItem("selectedDivision");
    if (stored && !isEditMode) {
      try {
        const division = JSON.parse(stored);
        if (division?.division_id) {
          // Auto-populate division field
          handleFormChange("division", division.division_id);
        }
      } catch (e) {
        console.error("Failed to parse selectedDivision:", e);
      }
    }
  }, [isEditMode]); // Run only once on mount

  // -------------------- Handle Form Change --------------------
 const handleFormChange = async (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
    setShowValidationSummary(false);
  };

  // -------------------- Handle Row Change --------------------
  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...rows];
    let row = { ...updatedRows[index] };

    switch (key) {
      case "category":
        row = {
          ...row,
          category: value,
          sub_category: null,
          item: null,
          ItemType: null,
          size: null,
          uom: "",
        };
        if (value) {
          const subcats = await fetchSubCategories(value.id, page, searchTerm);
          setSubcategories((prev) => ({
            ...prev,
            [index]: subcats,
          }));
        }
        break;

      case "sub_category":
        row = {
          ...row,
          sub_category: value,
          item: null,
          ItemType: null,
          size: null,
          uom: "",
        };
        if (row.category && value) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            row.category.id,
            value.subcat_id,
            page,
            searchTerm,
          );
          const mappedItems = (itemData.results || []).map((item) => ({
            id: item.id,
            name: item.name,
            item_code: item.item_code || item.code || "",
            unit_name: item.unit_name || item.uom || "",
            current_stock: item.current_stock || 0,
            size_id: item.size || null,
            size_name: item.size_name || "",
            store_item_type_id: item.store_item_type || null,
            store_item_type_name: item.store_item_type_name || "",
            unit_id: item.unit || null,
          }));

          setItems((prev) => ({
            ...prev,
            [index]: mappedItems,
          }));
        }
        break;

      case "item":
        const newRow = { ...row };

        newRow.stock_qty = value?.current_stock || "";

        if (!newRow.size && value?.size_id) {
          const foundSize = size.find(
            (s) =>
              String(s.id) === String(value.size_id) ||
              s.name === value.size_name ||
              s.size === value.size_name,
          );
          if (foundSize) {
            newRow.size = foundSize;
          }
        }

        if (!newRow.uom && value?.unit_name) {
          const unit = unitOptions.find(
            (u) =>
              u.name === value.unit_name ||
              String(u.id) === String(value.unit_id),
          );
          if (unit) {
            newRow.uom = unit.id;
          }
        }

        if (!newRow.ItemType && value?.store_item_type_id) {
          const foundItemType = itemTypes.find(
            (it) =>
              String(it.id) === String(value.store_item_type_id) ||
              it.name === value.store_item_type_name ||
              it.ItemType === value.store_item_type_name,
          );
          if (foundItemType) {
            newRow.ItemType = foundItemType;
          }
        }

        newRow.item = value;

        newRow.indent_qty = Math.max(
          (parseFloat(newRow.required_qty) || 0) -
            (parseFloat(newRow.stock_qty) || 0),
          0,
        );

        row = newRow;
        break;

      case "ItemType":
        // Explicitly handle null/undefined - allows clearing
        row = { ...row, ItemType: value === undefined ? null : value };
        break;

      case "size":
        // value is the full object from Autocomplete or null when cleared
        row = { ...row, size: value };
        break;

      case "uom":
        // value is the ID (string/number) or empty string when cleared
        row = { ...row, uom: value };
        break;

      case "item_description":
        row = { ...row, item_description: value };
        break;

      case "required_qty":
      case "stock_qty":
        row = { ...row, [key]: value };
        row.indent_qty = Math.max(
          (parseFloat(row.required_qty) || 0) -
            (parseFloat(row.stock_qty) || 0),
          0,
        );
        break;

      case "estimated_rate":
      case "indent_qty":
        row = { ...row, [key]: value };
        break;

      default:
        row[key] = value;
    }

    row.estimated_value = (
      (parseFloat(row.indent_qty) || 0) * (parseFloat(row.estimated_rate) || 0)
    ).toFixed(2);

    updatedRows[index] = row;
    setRows(updatedRows);

    // Clear row errors when user makes changes
    setRowErrors((prev) => {
      const newErrors = [...prev];
      if (newErrors[index]) {
        newErrors[index] = { ...newErrors[index], [key]: "" };
      }
      return newErrors;
    });
  };

  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (!newValue || newValue.trim() === "") {
        if (type === "category") {
          const catData = await getCategories();
          const list = Array.isArray(catData.results)
            ? catData.results
            : catData;
          setCategories(list);
        }

        if (type === "sub_category" && row.category) {
          const subcatData = await fetchSubCategories(row.category.id);
          setSubcategories((prev) => ({
            ...prev,
            [index]: subcatData,
          }));
        }
        if (type === "item" && row.category && row.sub_category) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            row.category.id,
            row.sub_category.subcat_id,
          );
          const mappedItems = (itemData.results || []).map((item) => ({
            id: item.id,
            name: item.name,
            item_code: item.item_code || item.code || "",
            unit_name: item.unit_name || item.uom || "",
            current_stock: item.current_stock,
            size_id: item.size || null,
            size_name: item.size_name || "",
            store_item_type_id: item.store_item_type || null,
            store_item_type_name: item.store_item_type_name || "",
            unit_id: item.unit || null,
          }));
          setItems((prev) => ({
            ...prev,
            [index]: mappedItems,
          }));
        }
        return;
      }
      if (type === "category") {
        const catData = await getCategories(1, newValue || "");
        const list = Array.isArray(catData.results) ? catData.results : catData;

        setCategories(list);
      }
      if (type === "sub_category" && row.category) {
        const subcatData = await fetchSubCategories(
          row.category.id,
          1,
          newValue || "",
        );
        setSubcategories((prev) => ({
          ...prev,
          [index]: subcatData,
        }));
      }
      if (type === "item" && row.category && row.sub_category) {
        const itemData = await fetchItemsByCategoryAndSubcategory(
          row.category.id,
          row.sub_category.subcat_id,
          1,
          newValue || "",
        );

        const mappedItems = (itemData.results || []).map((item) => ({
          id: item.id,
          name: item.name,
          item_code: item.item_code || item.code || "",
          unit_name: item.unit_name || item.uom || "",
          current_stock: item.current_stock,
          size_id: item.size || null,
          size_name: item.size_name || "",
          store_item_type_id: item.store_item_type || null,
          store_item_type_name: item.store_item_type_name || "",
          unit_id: item.unit || null,
        }));

        setItems((prev) => ({
          ...prev,
          [index]: mappedItems,
        }));
      }

      if (type === "ItemType") {
        try {
          const itemTypeData = await getItemType(newValue || "");
          const list = Array.isArray(itemTypeData.results)
            ? itemTypeData.results
            : itemTypeData;
          setItemTypes(list);
        } catch (err) {
          console.error("Error fetching item types:", err);
        }
      }

      if (type === "size") {
        try {
          const sizeData = await getSizes(newValue || "");
          const list = Array.isArray(sizeData.results)
            ? sizeData.results
            : sizeData;
          setSizes(list);
        } catch (err) {
          console.error("Error fetching sizes:", err);
        }
      }
    }, 500),
    [],
  );

  // -------------------- Add / Delete Rows --------------------
  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
    setRowErrors((prev) => [...prev, {}]);
    setShowValidationSummary(false);
  };

  const handleDeleteRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowErrors((prev) => prev.filter((_, i) => i !== index));
    setShowValidationSummary(false);
  };

  // -------------------- Validate Form --------------------
  const validateForm = () => {
    const errors = {};
    const rowErrs = [];
    const messages = [];

    // Header validations
    if (!formData.indent_no) {
      errors.indent_no = "Indent No. is required";
      messages.push("Indent No. is required");
    }
    if (!formData.indent_type) {
      errors.indent_type = "Indent Type is required";
      messages.push("Indent Type is required");
    }
    if (!formData.form_remarks) {
      errors.form_remarks = "Remarks are required";
      messages.push("Remarks are required");
    }
    if (!formData.project) {
      errors.project = "Project is required";
      messages.push("Project is required");
    }

    // Row validations
    rows.forEach((row, i) => {
      const err = {};

      if (!row.category) {
        err.category = "Required";
        messages.push(`Row ${i + 1}: Category is required`);
      }
      if (!row.sub_category) {
        err.sub_category = "Required";
        messages.push(`Row ${i + 1}: Sub Category is required`);
      }
      if (!row.item) {
        err.item = "Required";
        messages.push(`Row ${i + 1}: Item Name is required`);
      }
      if (!row.required_qty || row.required_qty <= 0) {
        err.required_qty = "Required (must be > 0)";
        messages.push(
          `Row ${
            i + 1
          }: Required Quantity is required and must be greater than 0`,
        );
      }
      if (!row.estimated_rate || row.estimated_rate <= 0) {
        err.estimated_rate = "Required (must be > 0)";
        messages.push(
          `Row ${i + 1}: Estimated Rate is required and must be greater than 0`,
        );
      }

      // Check if indent_qty is valid
      const indentQty = parseFloat(row.indent_qty) || 0;
      if (indentQty <= 0) {
        messages.push(
          `Row ${
            i + 1
          }: Indent Quantity should be greater than 0. Please check Required Qty and Stock Qty`,
        );
      }

      rowErrs[i] = err;
    });

    // Check if at least one row exists
    if (rows.length === 0) {
      messages.push("At least one item is required");
    }

    setFormErrors(errors);
    setRowErrors(rowErrs);
    setValidationMessages(messages);
    setShowValidationSummary(messages.length > 0);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0)
    );
  };

  // -------------------- Submit Form --------------------
  const grandTotal = rows.reduce(
    (sum, row) => sum + parseFloat(row.estimated_value || 0),
    0,
  );

  const handleSubmit = async () => {
     const divisionData = getSelectedDivision();

    // ✅ Validate division
    if (!divisionData?.division_id) {
      toast.error("No division selected. Please select a division from the header.");
      return;
    }
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        division: divisionData.division_id, 
        items: rows.map((row, idx) => {
          const existingItem = isEditMode
            ? location.state.data.items[idx]
            : null;

          // Default values for non-required fields
          return {
            category: row.category?.id || existingItem?.category || "",
            sub_category:
              row.sub_category?.subcat_id || existingItem?.sub_category || "",
            item: row.item?.id || existingItem?.item,
            // Handle null explicitly - don't fall back to existing value if user cleared it
            ItemType: row.ItemType?.id ?? null,
            size: row.size?.id ?? null,
            uom: row.uom || "", // Empty string for cleared UOM
            item_description: row.item_description || "",
            required_qty: row.required_qty || 0,
            stock_qty: row.stock_qty || 0,
            indent_qty: row.indent_qty || 0,
            previous_purchase_rate: row.previous_purchase_rate || 0,
            estimated_rate: row.estimated_rate || 0,
            estimated_value: row.estimated_value || 0,
            cost_center: row.cost_center || "",
            department_name: formData.department_name || "",
          };
        }),
        total_amount: grandTotal.toFixed(2),
        // Ensure all form fields have default values
        department_name: formData.department_name || "",
        form_remarks: formData.form_remarks || "",
        indent_type: formData.indent_type || "",
        indent_no: formData.indent_no || "",
        division: formData.division || "",
        project: formData.project || "",
        created_by: formData.created_by || localStorage.getItem("user_id"),
      };

      let response;
      if (isEditMode) {
        const indentId =
          location?.state?.data?.indent_id || location?.state?.data?.id;
        response = await IndentUpdate(indentId, payload);
      } else {
        response = await IndentCreate(payload);
      }

      if (response) {
        toast.success(
          `Indent ${isEditMode ? "updated" : "submitted"} successfully!`,
        );

        sessionStorage.removeItem("indentDraftHeader");
        sessionStorage.removeItem("indentDraftRows");

        setFormData({
          division: "",
          indent_type: "",
          form_remarks: "",
          department_name: "",
          project: "",
          indent_no: "",
          created_by: localStorage.getItem("user_id"),
        });
        setRows([createEmptyRow()]);

        if (isEditMode) navigate("/dashboard/indent-list");
      } else {
        toast.error(`Failed to ${isEditMode ? "update" : "submit"} indent.`);
      }
    } catch (err) {
      toast.error("Something went wrong. Please check the form.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- Modal Close --------------------
  const handleCloseItemModal = async () => {
    setOpenItemModal(false);
  };

  const handleSaveDraft = () => {
    sessionStorage.setItem("indentDraftHeader", JSON.stringify(formData));
    sessionStorage.setItem("indentDraftRows", JSON.stringify(rows));
    toast.info("Draft saved successfully!");
  };

  useEffect(() => {
    const draftHeader = sessionStorage.getItem("indentDraftHeader");
    const draftRows = sessionStorage.getItem("indentDraftRows");

    if (draftHeader && draftRows) {
      setFormData(JSON.parse(draftHeader));
      setRows(JSON.parse(draftRows));
    }
  }, []);

  // -------------------- JSX --------------------
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

       {/* ✅ Show warning if no division selected */}
      {!storedDivision?.division_id && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No division selected. Please select a division from the header to
          continue.
        </Alert>
      )}

      {/* Validation Summary Alert */}
      {showValidationSummary && validationMessages.length > 0 && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setShowValidationSummary(false)}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Please fix the following errors:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
            {validationMessages.map((msg, idx) => (
              <li key={idx}>
                <Typography variant="body2">{msg}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* ✅ Buttons - Desktop same, Mobile stack */}
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: -4,
            pt: 0,
            mb: 2,
            px: 1,
            py: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              sessionStorage.removeItem("indentDraftHeader");
              sessionStorage.removeItem("indentDraftRows");
              window.history.back();
            }}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            ← Back
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
              color: "#ff8400ff",
              borderColor: "#ff8400ff",
            }}
          >
            Draft
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !storedDivision?.division_id} // ✅ Disable if no division
            sx={{
              borderRadius: 0,
              height: 35,
              fontSize: "0.7rem",
              textTransform: "none",
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      ) : (
        <Box sx={{ mt: -4, pt: 0, mb: 2, px: 1, py: 1 }}>
          <Grid container justifyContent="flex-end" gap={3} mb={2}>
            <Button
              variant="outlined"
              onClick={() => {
                sessionStorage.removeItem("indentDraftHeader");
                sessionStorage.removeItem("indentDraftRows");
                window.history.back();
              }}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              ← Back
            </Button>
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
                color: "#ff8400ff",
                borderColor: "#ff8400ff",
              }}
            >
              Draft
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !storedDivision?.division_id} // ✅ Disable if no division
              sx={{
                borderRadius: 0,
                height: 35,
                minWidth: 120,
                fontSize: "0.7rem",
                px: 2,
                textTransform: "none",
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Grid>
        </Box>
      )}

      {/* ✅ Form Fields - Desktop same, Mobile full width */}
      <Box border="1px solid #ccc" borderRadius="8px" p={2} bgcolor="#fff">
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Division"
              required
              value={storedDivision?.division || "No Division Selected"}
              disabled
              InputProps={{
                sx: {
                  backgroundColor: "#f5f5f5",
                },
              }}
              helperText="Division is set globally"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              fullWidth
              options={projectOptions}
              getOptionLabel={(opt) => opt.project_name || ""}
              value={
                (Array.isArray(projectOptions) &&
                  projectOptions.find((opt) => opt.id === formData.project)) ||
                null
              }
              onChange={(e, val) => handleFormChange("project", val?.id || "")}
              onInputChange={(e, newInputValue) => {
                setProjectSearch(newInputValue);
              }}
              disabled={!storedDivision?.division_id} // ✅ Disable if no division
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Project"
                  error={!!formErrors.project}
                  helperText={formErrors.project || "Select a project"}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              required
              fullWidth
              label="Indent No."
              value={formData.indent_no}
              disabled
              error={!!formErrors.indent_no}
              helperText={formErrors.indent_no || "Auto-generated"}
              InputProps={{
                style: { height: "40px" },
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              required
              fullWidth
              label="Indent Type"
              value={formData.indent_type}
              onChange={(e) => handleFormChange("indent_type", e.target.value)}
              error={!!formErrors.indent_type}
              helperText={formErrors.indent_type || "Enter indent type"}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              required
              fullWidth
              label="Remarks"
              value={formData.form_remarks}
              onChange={(e) => handleFormChange("form_remarks", e.target.value)}
              error={!!formErrors.form_remarks}
              helperText={formErrors.form_remarks || "Enter remarks"}
            />
          </Grid>
        </Grid>

        {/* ✅ Table Section - Desktop same, Mobile card layout */}
        <Box mt={3}>
          {isMobile ? (
            <Box>
              {rows.map((row, index) => (
                <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Item #{index + 1}
                    </Typography>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* Category */}
                      <Box>
                        <Autocomplete
                          options={Array.isArray(categories) ? categories : []}
                          getOptionLabel={(opt) => opt.name}
                          value={row.category || null}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear" || !val) {
                              await handleItemInputChange(
                                index,
                                row,
                                "",
                                "category",
                              );
                              handleInputChange(index, "category", null);
                              return;
                            }
                            handleInputChange(index, "category", val);
                          }}
                          onInputChange={(e, newValue, reason) => {
                            if (reason === "input") {
                              handleItemInputChange(
                                index,
                                row,
                                newValue,
                                "category",
                              );
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Category"
                              required
                              size="small"
                              error={!!rowErrors[index]?.category}
                            />
                          )}
                        />
                        {rowErrors[index]?.category && (
                          <FormHelperText error sx={{ ml: 2 }}>
                            {rowErrors[index].category}
                          </FormHelperText>
                        )}
                      </Box>

                      {/* Subcategory */}
                      <Box>
                        <Autocomplete
                          options={subcategories[index] || []}
                          getOptionLabel={(opt) => opt.name}
                          value={row.sub_category || null}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear" || !val) {
                              await handleItemInputChange(
                                index,
                                row,
                                "",
                                "sub_category",
                              );
                              handleInputChange(index, "sub_category", null);
                              return;
                            }
                            handleInputChange(index, "sub_category", val);
                          }}
                          onInputChange={(e, newValue) =>
                            handleItemInputChange(
                              index,
                              row,
                              newValue,
                              "sub_category",
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Subcategory"
                              required
                              size="small"
                              error={!!rowErrors[index]?.sub_category}
                            />
                          )}
                        />
                        {rowErrors[index]?.sub_category && (
                          <FormHelperText error sx={{ ml: 2 }}>
                            {rowErrors[index].sub_category}
                          </FormHelperText>
                        )}
                      </Box>

                      {/* Item */}
                      <Box>
                        <Autocomplete
                          options={items[index] || []}
                          getOptionLabel={(opt) => opt.name || ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          value={row.item}
                          onChange={async (e, val, reason) => {
                            if (reason === "clear" || !val) {
                              await handleItemInputChange(
                                index,
                                row,
                                "",
                                "item",
                              );
                              handleInputChange(index, "item", null);
                              return;
                            }
                            handleInputChange(index, "item", val);
                          }}
                          onInputChange={(e, newValue) =>
                            handleItemInputChange(index, row, newValue, "item")
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Item Name"
                              required
                              size="small"
                              error={!!rowErrors[index]?.item}
                            />
                          )}
                        />
                        {rowErrors[index]?.item && (
                          <FormHelperText error sx={{ ml: 2 }}>
                            {rowErrors[index].item}
                          </FormHelperText>
                        )}
                      </Box>

                      {/* Size (Optional) */}
                      <Autocomplete
                        options={size}
                        getOptionLabel={(opt) => opt?.size || opt?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={row.size || null}
                        onChange={(e, val, reason) => {
                          handleInputChange(index, "size", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleItemInputChange(index, row, newValue, "size");
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Size" size="small" />
                        )}
                      />

                      {/* UOM (Optional) */}
                      <Autocomplete
                        options={unitOptions}
                        getOptionLabel={(opt) => opt.name}
                        value={
                          unitOptions.find(
                            (u) => String(u.id) === String(row.uom),
                          ) || null
                        }
                        onChange={(e, val) => {
                          const value = val ? val.id : "";
                          handleInputChange(index, "uom", value);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="UOM" size="small" />
                        )}
                      />

                      {/* Number Fields */}
                      <Box>
                        <TextField
                          fullWidth
                          type="number"
                          size="small"
                          label="Required Qty"
                          value={row.required_qty ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "required_qty",
                              e.target.value,
                            )
                          }
                          error={!!rowErrors[index]?.required_qty}
                          helperText={
                            rowErrors[index]?.required_qty ||
                            "Enter required quantity"
                          }
                        />
                      </Box>

                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        label="Stock Qty"
                        value={row.stock_qty ?? ""}
                        onChange={(e) =>
                          handleInputChange(index, "stock_qty", e.target.value)
                        }
                      />

                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        label="Indent Qty"
                        value={row.indent_qty ?? ""}
                        onChange={(e) =>
                          handleInputChange(index, "indent_qty", e.target.value)
                        }
                        error={parseFloat(row.indent_qty || 0) <= 0}
                        helperText={
                          parseFloat(row.indent_qty || 0) <= 0
                            ? "Indent quantity should be greater than 0"
                            : ""
                        }
                      />

                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        label="Previous Purchase Rate"
                        value={row.previous_purchase_rate ?? ""}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "previous_purchase_rate",
                            e.target.value,
                          )
                        }
                      />

                      <Box>
                        <TextField
                          fullWidth
                          type="number"
                          size="small"
                          label="Estimated Rate"
                          value={row.estimated_rate ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "estimated_rate",
                              e.target.value,
                            )
                          }
                          error={!!rowErrors[index]?.estimated_rate}
                          helperText={
                            rowErrors[index]?.estimated_rate ||
                            "Enter estimated rate"
                          }
                        />
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        label="Estimated Value"
                        value={row.estimated_value}
                        disabled
                      />

                      {/* ✅ Item Description - Mobile में last में */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Item Description"
                        value={row.item_description || ""}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "item_description",
                            e.target.value,
                          )
                        }
                        multiline
                        rows={2}
                      />

                      {/* Delete Button */}
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteRow(index)}
                        startIcon={<DeleteOutlineIcon />}
                      >
                        Delete Item
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: 80, maxWidth: 150 }}>
                      Sl. No.
                    </TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Sub Category</TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell>Required Qty.</TableCell>
                    <TableCell>Stock Qty.</TableCell>
                    <TableCell>Indent Qty.</TableCell>
                    <TableCell>Previous Rate</TableCell>
                    <TableCell>Estimated Rate</TableCell>
                    <TableCell>Estimated Value</TableCell>
                    <TableCell>Item Description</TableCell>{" "}
                    {/* ✅ Last column में */}
                    <TableCell>Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={index}
                      hover={false}
                      sx={{
                        "&:hover": {
                          backgroundColor: "inherit !important",
                        },
                        backgroundColor:
                          rowErrors[index] &&
                          Object.keys(rowErrors[index]).length > 0
                            ? "#ffebee"
                            : "inherit",
                      }}
                    >
                      <TableCell sx={{ textAlign: "center" }}>
                        {index + 1}
                      </TableCell>
                      {/* Category */}
                      <TableCell>
                        <Box>
                          <Autocomplete
                            options={
                              Array.isArray(categories)
                                ? categories
                                : categories
                            }
                            getOptionLabel={(opt) => opt.name}
                            value={row.category || null}
                            onChange={async (e, val, reason) => {
                              if (reason === "clear" || !val) {
                                await handleItemInputChange(
                                  index,
                                  row,
                                  "",
                                  "category",
                                );
                                handleInputChange(index, "category", null);
                                return;
                              }
                              handleInputChange(index, "category", val);
                            }}
                            onInputChange={(e, newValue, reason) => {
                              if (reason === "input") {
                                handleItemInputChange(
                                  index,
                                  row,
                                  newValue,
                                  "category",
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    height: "5px",
                                  },
                                }}
                                required
                                error={!!rowErrors[index]?.category}
                              />
                            )}
                            sx={{ minWidth: 190 }}
                          />
                          {rowErrors[index]?.category && (
                            <FormHelperText
                              error
                              sx={{ ml: 1, fontSize: "0.75rem" }}
                            >
                              {rowErrors[index].category}
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>
                      {/* Subcategory */}
                      <TableCell>
                        <Box>
                          <Autocomplete
                            options={subcategories[index] || []}
                            getOptionLabel={(opt) => opt.name}
                            value={row.sub_category || null}
                            onChange={async (e, val, reason) => {
                              if (reason === "clear" || !val) {
                                await handleItemInputChange(
                                  index,
                                  row,
                                  "",
                                  "sub_category",
                                );
                                handleInputChange(index, "sub_category", null);
                                return;
                              }
                              handleInputChange(index, "sub_category", val);
                            }}
                            onInputChange={(e, newValue) =>
                              handleItemInputChange(
                                index,
                                row,
                                newValue,
                                "sub_category",
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    height: "5px",
                                  },
                                }}
                                fullWidth
                                label="Subcategory"
                                required
                                error={!!rowErrors[index]?.sub_category}
                              />
                            )}
                            sx={{ minWidth: 190 }}
                          />
                          {rowErrors[index]?.sub_category && (
                            <FormHelperText
                              error
                              sx={{ ml: 1, fontSize: "0.75rem" }}
                            >
                              {rowErrors[index].sub_category}
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>
                      {/* Item */}
                      <TableCell>
                        <Box>
                          <Autocomplete
                            options={items[index] || []}
                            getOptionLabel={(opt) => opt.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            value={row.item}
                            onChange={async (e, val, reason) => {
                              if (reason === "clear" || !val) {
                                await handleItemInputChange(
                                  index,
                                  row,
                                  "",
                                  "item",
                                );
                                handleInputChange(index, "item", null);
                                return;
                              }
                              handleInputChange(index, "item", val);
                            }}
                            onInputChange={(e, newValue) =>
                              handleItemInputChange(
                                index,
                                row,
                                newValue,
                                "item",
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    height: "5px",
                                  },
                                }}
                                label="Item"
                                required
                                error={!!rowErrors[index]?.item}
                              />
                            )}
                            sx={{ minWidth: 190 }}
                          />
                          {rowErrors[index]?.item && (
                            <FormHelperText
                              error
                              sx={{ ml: 1, fontSize: "0.75rem" }}
                            >
                              {rowErrors[index].item}
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>
                      {/* Size (Optional) */}
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={size || []}
                          getOptionLabel={(option) => {
                            if (!option) return "";
                            return option?.name || option?.size || "";
                          }}
                          value={row.size || null}
                          onChange={(e, newValue) => {
                            // This handles the actual selection
                            handleInputChange(index, "size", newValue);
                          }}
                          onInputChange={(e, newInputValue, reason) => {
                            // Only search when user is typing, not on selection or clear
                            if (reason === "input") {
                              handleItemInputChange(
                                index,
                                row,
                                newInputValue,
                                "size",
                              );
                            }
                            // Don't do anything on "reset" (after selection) or "clear"
                          }}
                          isOptionEqualToValue={(option, value) => {
                            if (!option && !value) return true;
                            if (!option || !value) return false;
                            return String(option.id) === String(value.id);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Size"
                              error={!!rowErrors[index]?.size}
                              helperText={rowErrors[index]?.size}
                            />
                          )}
                        />
                      </TableCell>
                      {/* UOM (Optional) */}
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={unitOptions || []}
                          getOptionLabel={(option) => {
                            if (!option) return "";
                            if (typeof option === "object") {
                              return option?.name || option?.unit_name || "";
                            }
                            const found = unitOptions.find(
                              (u) => String(u.id) === String(option),
                            );
                            return found?.name || "";
                          }}
                          value={
                            row.uom
                              ? unitOptions.find(
                                  (u) => String(u.id) === String(row.uom),
                                ) || null
                              : null
                          }
                          onChange={(e, newValue) => {
                            handleInputChange(index, "uom", newValue?.id || "");
                          }}
                          onInputChange={(e, newInputValue, reason) => {
                            // Only search when user is typing
                            if (reason === "input") {
                              // Add search logic if needed
                            }
                          }}
                          isOptionEqualToValue={(option, value) => {
                            if (!option && !value) return true;
                            if (!option || !value) return false;
                            return String(option.id) === String(value.id);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="UOM"
                              error={!!rowErrors[index]?.uom}
                              helperText={rowErrors[index]?.uom}
                            />
                          )}
                        />
                      </TableCell>
                      {/* Required Qty */}
                      <TableCell>
                        <Box>
                          <TextField
                            type="number"
                            size="small"
                            value={row.required_qty ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "required_qty",
                                e.target.value,
                              )
                            }
                            sx={{
                              width: "120px",
                              "& .MuiInputBase-root": {
                                height: 38,
                                fontSize: "0.8rem",
                              },
                              "& .MuiInputBase-input": {
                                padding: "4px 8px",
                              },
                            }}
                            error={!!rowErrors[index]?.required_qty}
                          />
                          {rowErrors[index]?.required_qty && (
                            <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                              Required
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>
                      {/* Stock Qty */}
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={row.stock_qty ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "stock_qty",
                              e.target.value,
                            )
                          }
                          sx={{
                            width: "120px",
                            "& .MuiInputBase-root": {
                              height: 38,
                              fontSize: "0.8rem",
                            },
                            "& .MuiInputBase-input": {
                              padding: "4px 8px",
                            },
                          }}
                        />
                      </TableCell>
                      {/* Indent Qty */}
                      <TableCell>
                        <Box>
                          <TextField
                            type="number"
                            size="small"
                            value={row.indent_qty ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "indent_qty",
                                e.target.value,
                              )
                            }
                            sx={{
                              width: "120px",
                              "& .MuiInputBase-root": {
                                height: 38,
                                fontSize: "0.8rem",
                              },
                              "& .MuiInputBase-input": {
                                padding: "4px 8px",
                              },
                            }}
                            error={parseFloat(row.indent_qty || 0) <= 0}
                          />
                          {parseFloat(row.indent_qty || 0) <= 0 && (
                            <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                              Should be &gt; 0
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>
                      {/* Previous Purchase Rate */}
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={row.previous_purchase_rate ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "previous_purchase_rate",
                              e.target.value,
                            )
                          }
                          sx={{
                            width: "120px",
                            "& .MuiInputBase-root": {
                              height: 38,
                              fontSize: "0.8rem",
                            },
                            "& .MuiInputBase-input": {
                              padding: "4px 8px",
                            },
                          }}
                        />
                      </TableCell>
                      {/* Estimated Rate */}
                      <TableCell>
                        <Box>
                          <TextField
                            type="number"
                            size="small"
                            value={row.estimated_rate ?? ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "estimated_rate",
                                e.target.value,
                              )
                            }
                            sx={{
                              width: "120px",
                              "& .MuiInputBase-root": {
                                height: 38,
                                fontSize: "0.8rem",
                              },
                              "& .MuiInputBase-input": {
                                padding: "4px 8px",
                              },
                            }}
                            error={!!rowErrors[index]?.estimated_rate}
                          />
                          {rowErrors[index]?.estimated_rate && (
                            <FormHelperText error sx={{ fontSize: "0.7rem" }}>
                              Required
                            </FormHelperText>
                          )}
                        </Box>
                      </TableCell>
                      {/* Estimated Value */}
                      <TableCell>
                        <input
                          readOnly
                          value={row.estimated_value}
                          style={{
                            width: "120px",
                            height: "38px",
                            fontSize: "0.875rem",
                            border: "1px solid #c4c4c4",
                            borderRadius: "4px",
                            backgroundColor: "#f5f5f5",
                            boxSizing: "border-box",
                          }}
                        />
                      </TableCell>
                      {/* ✅ Item Description - Last column में */}
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.item_description || ""}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "item_description",
                              e.target.value,
                            )
                          }
                          sx={{
                            width: "200px",
                            "& .MuiInputBase-root": {
                              height: 38,
                              fontSize: "0.8rem",
                            },
                            "& .MuiInputBase-input": {
                              padding: "4px 8px",
                            },
                          }}
                        />
                      </TableCell>
                      {/* Delete */}
                      <TableCell>
                        <Tooltip title="Delete Row">
                          <IconButton
                            onClick={() => handleDeleteRow(index)}
                            color="error"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Add Row */}
          <Box mt={2} textAlign="center">
            <Tooltip title="Add Row">
              <IconButton onClick={handleAddRow} color="primary">
                <AddCircleOutlineIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Grand Total */}
          <Box mt={2} textAlign="right" pr={isMobile ? 2 : 9}>
            <strong>Grand Total: ₹ {grandTotal.toFixed(2)}</strong>
          </Box>
        </Box>
      </Box>

      {/* Add Item Modal */}
      <Dialog
        open={openItemModal}
        onClose={handleCloseItemModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add Item</DialogTitle>
        <DialogContent>
          <AddItemForm onClose={handleCloseItemModal} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IndentForm;
